import { useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Table, Thead, Tbody, Tr, Th, Td } from '../../components/ui/table';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api-client';
import { useToast } from '../../components/ui/toast';
import type { SlaPolicy } from '../../types';
import { PRIORITY_LABELS, ALL_PRIORITIES } from '../../lib/constants';

export function SlaPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['sla-policies'],
    queryFn: () => api.get<SlaPolicy[]>('/sla/policies'),
  });
  const policies = data?.data ?? [];

  const [editing, setEditing] = useState<Partial<SlaPolicy> | null>(null);

  const saveMutation = useMutation({
    mutationFn: (p: Partial<SlaPolicy>) =>
      p.id ? api.patch(`/sla/policies/${p.id}`, p) : api.post('/sla/policies', p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla-policies'] }); setEditing(null);
      toast('success', 'Politica SLA a fost salvată cu succes');
    },
    onError: (e) => toast('error', `Eroare la salvare: ${e.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/sla/policies/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sla-policies'] });
      toast('success', 'Politica SLA a fost ștearsă');
    },
    onError: (e) => toast('error', `Eroare la ștergere: ${e.message}`),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-dark-text">Politici SLA</h1>
        <Button onClick={() => setEditing({ name: '', priority: 'NORMAL', firstResponseMinutes: 60, resolutionMinutes: 480 })}>
          <Plus size={18} /> Adaugă
        </Button>
      </div>

      <Card>
        <Table>
          <Thead>
            <Tr>
              <Th>Nume</Th>
              <Th>Prioritate</Th>
              <Th>Timp răspuns (min)</Th>
              <Th>Timp rezolvare (min)</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <Tr key={i}><Td colSpan={5}><Skeleton className="h-4 w-full" /></Td></Tr>
            ))}
            {policies.map((p) => (
              <Tr key={p.id}>
                <Td className="font-medium">{p.name}</Td>
                <Td>{PRIORITY_LABELS[p.priority]}</Td>
                <Td>{p.firstResponseMinutes}</Td>
                <Td>{p.resolutionMinutes}</Td>
                <Td className="flex gap-1">
                  <Button size="sm" variant="ghost" onClick={() => setEditing(p)}><Pencil size={14} /></Button>
                  <Button size="sm" variant="ghost" onClick={() => { if (confirm('Ștergi politica?')) deleteMutation.mutate(p.id); }}><Trash2 size={14} /></Button>
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Card>

      {editing && (
        <Dialog open onClose={() => setEditing(null)} title={editing.id ? 'Editează politica SLA' : 'Politică SLA nouă'}>
          <div className="space-y-4">
            <Input label="Nume" value={editing.name ?? ''} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
            <Select
              label="Prioritate"
              options={ALL_PRIORITIES.map((p) => ({ value: p, label: PRIORITY_LABELS[p] }))}
              value={editing.priority ?? 'NORMAL'}
              onChange={(e) => setEditing({ ...editing, priority: e.target.value as SlaPolicy['priority'] })}
            />
            <Input label="Timp răspuns (minute)" type="number" value={String(editing.firstResponseMinutes ?? 60)} onChange={(e) => setEditing({ ...editing, firstResponseMinutes: Number(e.target.value) })} onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }} />
            <Input label="Timp rezolvare (minute)" type="number" value={String(editing.resolutionMinutes ?? 480)} onChange={(e) => setEditing({ ...editing, resolutionMinutes: Number(e.target.value) })} onKeyDown={(e) => { if (['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }} />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(null)}>Anulează</Button>
              <Button onClick={() => saveMutation.mutate(editing)} disabled={saveMutation.isPending}>Salvează</Button>
            </div>
          </div>
        </Dialog>
      )}
    </div>
  );
}
